import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import Paper from "@mui/material/Paper";
import officeLocationsApiService, {
  OfficeLocation,
} from "../../services/office-locations-api";

interface OfficeLocationsCardProps {
  onSave?: () => void;
  hideHeader?: boolean;
  shouldLoad?: boolean;
}

export interface OfficeLocationsCardRef {
  openAddDialog: () => void;
}

const OfficeLocationsCard = forwardRef<
  OfficeLocationsCardRef,
  OfficeLocationsCardProps
>(({ onSave, hideHeader = false, shouldLoad = false }, ref) => {
  const { t } = useTranslation();
  const [locations, setLocations] = useState<OfficeLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<OfficeLocation>({
    name: "",
  });

  useEffect(() => {
    if (shouldLoad) {
      loadLocations();
    }
  }, [shouldLoad]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    openAddDialog: () => handleOpenDialog(),
  }));

  const loadLocations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await officeLocationsApiService.getAllOfficeLocations();
      setLocations(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("config.errorLocationLoad"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (location?: OfficeLocation) => {
    if (location) {
      setEditingId(location.id || null);
      setFormData(location);
    } else {
      setEditingId(null);
      setFormData({ name: "" });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
    setFormData({ name: "" });
  };

  const handleSaveLocation = async () => {
    if (!formData.name.trim()) {
      setError(t("config.locationRequired"));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (editingId) {
        // Update existing location
        const updatedLocations =
          await officeLocationsApiService.updateOfficeLocation(
            editingId,
            formData.name,
          );
        setLocations(updatedLocations);
        setSuccess(t("config.locationUpdated"));
      } else {
        // Add new location
        const updatedLocations =
          await officeLocationsApiService.addOfficeLocation(formData.name);
        setLocations(updatedLocations);
        setSuccess(t("config.locationCreated"));
      }

      handleCloseDialog();
      onSave?.();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("config.errorLocation"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      sx={{
        mb: hideHeader ? 0 : 3,
        borderRadius: 2,
        boxShadow: hideHeader ? "none" : "0 2px 8px rgba(0,0,0,0.08)",
      }}
    >
      <CardContent sx={{ p: hideHeader ? 0 : 3 }}>
        {!hideHeader && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              📍 {t("config.officeLocations")}
            </Typography>
            <Tooltip title={t("config.addNewOfficeLocation")}>
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                variant="contained"
                size="small"
              >
                {t("common.add")}
              </Button>
            </Tooltip>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        {loading && !locations.length ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("config.locationName")}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, width: 100 }}>
                    {t("common.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        {t("config.noLocations")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  locations.map((location) => (
                    <TableRow key={location.id} hover>
                      <TableCell>{location.name}</TableCell>
                      <TableCell align="right">
                        <Tooltip title={t("config.tooltipEdit")}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(location)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 2 }}
        >
          {t("config.officeLocationsNote")}
        </Typography>
      </CardContent>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingId ? t("config.editLocation") : t("config.addLocation")}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ pt: 2 }}>
            <TextField
              autoFocus
              label={t("config.locationName")}
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              fullWidth
              variant="outlined"
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSaveLocation}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
});

OfficeLocationsCard.displayName = "OfficeLocationsCard";

export default React.memo(OfficeLocationsCard);
